import { z } from 'zod';
/**
 * Chapsi Showcase exports (docs/adr/0071, SC-14): the design set as a PDF (screens in order, optionally with a cover,
 * pins and comments), the screens as a ZIP of PNGs, the feedback as a CSV, and the approval certificate phase D stored.
 * PDFs and ZIPs are built by the worker and kept for a day; every download is a 5-minute signed link for the person who
 * asked. Clients export only what they can see (the latest published version, never internal comments or notes), and
 * only when the set's share settings allow downloads.
 */
export const DesignExportKind = z.enum(['pdf', 'images', 'csv']);
export const DESIGN_EXPORT_KIND_LABELS = { pdf: 'PDF', images: 'Images', csv: 'Feedback list (CSV)' };
export const DesignExportStatus = z.enum(['queued', 'running', 'ready', 'failed', 'expired']);
/** A ready file is kept this long; each download link lasts 5 minutes. */
export const DESIGN_EXPORT_KEEP_HOURS = 24;
/** Exports one person may ask for in a workspace per day (failed ones don't count). */
export const DESIGN_EXPORTS_PER_DAY = 30;
export const DesignExportOptions = z.object({
    /** PDF: the comment pins on each screen and a page listing that screen's comments and replies. */
    pins: z.boolean().default(true),
    /** PDF: a cover page with the workspace, project, set, version and date. */
    cover: z.boolean().default(true),
    /** PDF and CSV: internal comments and notes as well (team copies only; never for clients). */
    internal: z.boolean().default(false),
    /** Images: 1x (the device size) or 2x (twice it, never above the uploaded size). */
    scale: z.union([z.literal(1), z.literal(2)]).default(2),
});
export const CreateDesignExportInput = DesignExportOptions.extend({
    kind: DesignExportKind,
    /** The team may export any version; clients always get the latest published one. Omit for the one in view. */
    versionNumber: z.number().int().min(1).max(10_000).optional(),
});
export const DesignExport = z.object({
    id: z.uuid(),
    kind: DesignExportKind,
    versionNumber: z.number().int(),
    status: DesignExportStatus,
    /** The download's name: "customer-app-v3.pdf". */
    fileName: z.string(),
    size: z.number().int().nullable(),
    pages: z.number().int().nullable(),
    /** A plain message when it failed. */
    error: z.string().nullable(),
    options: DesignExportOptions,
    createdAt: z.string(),
    finishedAt: z.string().nullable(),
    /** When the file is deleted (ready exports). */
    expiresAt: z.string().nullable(),
});
export const DesignExportList = z.object({
    /** The reader's own exports of this set, newest first (other people's are theirs). */
    items: z.array(DesignExport),
    /** The reader may export this set (the team always; clients when the set allows downloads). */
    canExport: z.boolean(),
    /** The reader may include internal comments and notes (the team only). */
    canIncludeInternal: z.boolean(),
    /** The version an export is made from by default: the team's current one, or the clients' published one. */
    versionNumber: z.number().int(),
    screenCount: z.number().int(),
    /** Comments the reader would get in the version (with internal ones counted apart, for the team). */
    comments: z.number().int(),
    internalComments: z.number().int(),
    /** The newest approval certificate the reader may download, or null. */
    certificate: z.object({ approvalId: z.uuid(), versionNumber: z.number().int(), name: z.string(), at: z.string() }).nullable(),
});
export const DesignExportDownload = z.object({ url: z.string(), fileName: z.string(), expiresAt: z.string() });
export const DesignCertificateQuery = z.object({ approvalId: z.uuid().optional() });
const def = (r) => r;
const id = (name) => z.object({ [name]: z.uuid() });
/** Spread into the registry in routes.ts. Every route needs the `showcase` feature; clients may call all four. */
export const showcaseExportRoutes = {
    /** The reader's exports of a set, what they may export, and the certificate they may download. */
    listDesignExports: def({ showcaseGuest: true,
        method: 'GET', path: '/v1/showcase/sets/:setId/exports', params: id('setId'), response: DesignExportList,
        isolation: { kind: 'resource', param: 'setId', resource: 'design_set' }, feature: 'showcase',
    }),
    /** Starts an export: the CSV is made at once, a PDF or ZIP on the worker (an email says when it is ready). */
    createDesignExport: def({ showcaseGuest: true, readOnlyAllowed: true,
        method: 'POST', path: '/v1/showcase/sets/:setId/exports', params: id('setId'), body: CreateDesignExportInput, response: DesignExport,
        isolation: { kind: 'resource', param: 'setId', resource: 'design_set' }, feature: 'showcase', exampleBody: { kind: 'csv' },
    }),
    /** A 5-minute link to a ready export, for the person who asked for it. */
    downloadDesignExport: def({ showcaseGuest: true,
        method: 'GET', path: '/v1/showcase/exports/:exportId/download', params: id('exportId'), response: DesignExportDownload,
        isolation: { kind: 'resource', param: 'exportId', resource: 'design_export' }, feature: 'showcase',
    }),
    /** A 5-minute link to an approval certificate (phase D stores it on approval): the newest, or the one named. */
    downloadDesignCertificate: def({ showcaseGuest: true,
        method: 'GET', path: '/v1/showcase/sets/:setId/certificate', params: id('setId'), query: DesignCertificateQuery, response: DesignExportDownload,
        isolation: { kind: 'resource', param: 'setId', resource: 'design_set' }, feature: 'showcase',
    }),
};
//# sourceMappingURL=showcase-exports.js.map